// SPDX-FileCopyrightText: Copyright (C) 2026 provide.io llc
// SPDX-License-Identifier: Apache-2.0

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthProvider } from "react-oidc-context";
import { Outlet } from "react-router";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { ErrorBoundary } from "@/components/shell/ErrorBoundary";
import { TopBar } from "@/components/shell/TopBar";
import { ApiClient } from "@/lib/api";
import { buildOidcConfig } from "@/lib/auth";
import { loadConfig, type RuntimeConfig } from "@/lib/config";

interface AppContextValue {
  readonly config: RuntimeConfig;
  readonly api: ApiClient;
}

const AppContext = createContext<AppContextValue | null>(null);

export function useApp(): AppContextValue {
  const v = useContext(AppContext);
  if (!v) throw new Error("useApp must be used inside Root");
  return v;
}

function tokenFromSessionStorage(
  issuer: string | undefined,
  clientId: string | undefined,
): string | null {
  if (!issuer || !clientId) return null;
  const raw = window.sessionStorage.getItem(`oidc.user:${issuer}:${clientId}`);
  if (!raw) return null;
  try {
    // biome-ignore lint/style/useNamingConvention: oidc-client-ts stores access_token in snake_case
    const parsed = JSON.parse(raw) as { access_token?: string };
    return parsed.access_token ?? null;
  } catch {
    return null;
  }
}

function ConfiguredProviders({
  config,
  children,
}: {
  readonly config: RuntimeConfig;
  readonly children: ReactNode;
}): ReactElement {
  const api = useMemo(
    () =>
      new ApiClient({
        baseUrl: config.apiBase,
        getToken: () => tokenFromSessionStorage(config.auth?.issuer, config.auth?.clientId),
      }),
    [config],
  );
  const queryClient = useMemo(() => new QueryClient(), []);
  const ctx: AppContextValue = { config, api };
  const tree = (
    <AppContext.Provider value={ctx}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </AppContext.Provider>
  );
  if (!config.auth) return tree;
  const oidc = buildOidcConfig(config.auth, { origin: window.location.origin });
  return <AuthProvider {...oidc}>{tree}</AuthProvider>;
}

export function Root(): ReactElement {
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    loadConfig()
      .then(setConfig)
      .catch((e: unknown) => setErr(String(e)));
  }, []);
  useEffect(() => {
    if (config?.branding.title) {
      document.title = config.branding.title;
    }
  }, [config]);
  if (err) return <div className="p-8 text-red-600 text-sm">{err}</div>;
  if (!config) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  return (
    <ErrorBoundary>
      <ConfiguredProviders config={config}>
        <div className="flex min-h-screen flex-col">
          <TopBar />
          <CommandPalette />
          <main className="flex-1">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </ConfiguredProviders>
    </ErrorBoundary>
  );
}
